import React from "react";
import { FiMaximize2, FiX } from "react-icons/fi";
import { formatFileSize, getFileKind, resolveImageUrl } from "../../utils/helpers";
import FileTypeBadge from "./FileTypeBadge";

const KIND_LABELS = {
  pdf: "PDF document",
  office: "Document",
  text: "Text file",
  video: "Video",
  audio: "Audio",
  other: "File",
};

const RemoveButton = ({ onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    title="Remove"
    aria-label="Remove attachment"
    className={`p-1.5 rounded-full bg-white text-red-500 hover:bg-red-50 shadow-sm border border-gray-100 transition-colors ${className}`}
  >
    <FiX className="w-3.5 h-3.5" />
  </button>
);

/**
 * Images as a thumbnail grid, other files as rows. Clicking any item calls
 * onOpen(index) with its position in `attachments`; onRemove is optional (admin).
 */
const AttachmentList = ({ attachments = [], onOpen, onRemove }) => {
  const items = attachments.map((file, index) => ({ file, index, kind: getFileKind(file) }));
  const images = items.filter((item) => item.kind === "image");
  const others = items.filter((item) => item.kind !== "image");

  if (items.length === 0) return null;

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map(({ file, index }) => (
            <div key={file._id || file.url} className="relative">
              <button
                type="button"
                onClick={() => onOpen(index)}
                title={`View ${file.name || "image"}`}
                className="group relative block w-full aspect-[4/3] rounded-xl overflow-hidden border border-gray-100 bg-gray-50"
              >
                <img
                  src={resolveImageUrl(file.url)}
                  alt={file.name || "Attachment"}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/35 transition-colors">
                  <FiMaximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </button>
              {onRemove && <RemoveButton onClick={() => onRemove(index)} className="absolute top-2 right-2" />}
            </div>
          ))}
        </div>
      )}

      {others.map(({ file, index, kind }) => (
        <div
          key={file._id || file.url}
          className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 hover:bg-primary-50/60 hover:border-primary-100 transition-colors"
        >
          <button
            type="button"
            onClick={() => onOpen(index)}
            className="flex items-center gap-3 flex-1 min-w-0 p-3 text-left"
          >
            <FileTypeBadge file={file} />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-gray-800 truncate">{file.name || "Attachment"}</span>
              <span className="block text-xs text-gray-400">
                {KIND_LABELS[kind] || "File"}
                {file.size ? ` · ${formatFileSize(file.size)}` : ""}
              </span>
            </span>
            <span className="hidden sm:flex items-center gap-1 text-xs font-semibold text-primary-500 flex-shrink-0">
              <FiMaximize2 className="w-3.5 h-3.5" /> View
            </span>
          </button>
          {onRemove && <RemoveButton onClick={() => onRemove(index)} className="mr-3" />}
        </div>
      ))}
    </div>
  );
};

export default AttachmentList;
